// The mindmap data model and every edit made to it. A map is plain JSON (the
// shape saved inside each .svg file, see engine/io.js). ADDITIVE ONLY: never
// rename a field or change what one means; old files must always open.
//
//   map  = { v: 1, kind: 'cth-mindmap', theme, layout, line, spacing,
//            root: node, floating: [node], rels: [rel] }
//   node = { id, text, children: [node],
//            collapsed?, color?, shape?, fill?, bold?, italic?, size?,
//            task? ('todo' | 'done'), note?, link?,
//            side? ('left' | 'right', first-level topics of a balanced map),
//            x?, y? (floating topics only: the topic's top-left on the canvas),
//            gx?, gy?, pin? (bubble layout: the circle's centre, and held where it was dropped) }
//   map.graph? = { style: 'bubbles' | 'dots', repel, distance, link, center }  (bubble layout; forces scale 0.2 to 3)
//   rel  = { id, from, to, label? }   a relationship line between any two topics

export const MAP_VERSION = 1;
export const LAYOUTS = ['balanced', 'right', 'left', 'down', 'up', 'tree', 'bubble'];
export const GRAPH_STYLES = ['bubbles', 'dots'];
export const LINES = ['curved', 'straight', 'elbow', 'rounded'];
export const SPACINGS = ['compact', 'normal', 'roomy'];
export const SHAPES = ['rounded', 'pill', 'rect', 'ellipse', 'underline', 'none'];
export const FILLS = ['solid', 'soft', 'outline'];
export const SIZES = ['s', 'm', 'l', 'xl'];

let n = 0;
/** A short id, unique within a map (and in practice across maps, so pasted branches never clash). */
export const uid = () => `${Date.now().toString(36).slice(-4)}${Math.random().toString(36).slice(2, 6)}${(n++ % 1296).toString(36)}`;

export function node(text = '', extra = {}) { return { id: uid(), text: String(text), children: [], ...extra }; }

export function blankMap(title = 'Central idea', opts = {}) {
  return {
    v: MAP_VERSION, kind: 'cth-mindmap',
    theme: opts.theme || 'colorful', layout: opts.layout || 'balanced', line: opts.line || 'curved', spacing: opts.spacing || 'normal',
    root: node(title), floating: [], rels: [],
  };
}

/** Fill in anything missing and repair ids, so a hand-edited or old file always opens. */
export function normalize(m) {
  const map = m && typeof m === 'object' ? m : {};
  map.v = map.v || MAP_VERSION;
  map.kind = 'cth-mindmap';
  if (!LAYOUTS.includes(map.layout)) map.layout = 'balanced';
  if (!LINES.includes(map.line)) map.line = 'curved';
  if (!SPACINGS.includes(map.spacing)) map.spacing = 'normal';
  if (typeof map.theme !== 'string') map.theme = 'colorful';
  if (!map.root || typeof map.root !== 'object') map.root = node('Central idea');
  if (!Array.isArray(map.floating)) map.floating = [];
  if (!Array.isArray(map.rels)) map.rels = [];
  if (map.graph != null && typeof map.graph !== 'object') delete map.graph;
  if (map.graph && !GRAPH_STYLES.includes(map.graph.style)) delete map.graph.style;
  const seen = new Set();
  const fix = (nd) => {
    if (!nd.id || seen.has(nd.id)) nd.id = uid();
    seen.add(nd.id);
    nd.text = nd.text == null ? '' : String(nd.text);
    if (!Array.isArray(nd.children)) nd.children = [];
    nd.children = nd.children.filter((c) => c && typeof c === 'object');
    nd.children.forEach(fix);
  };
  fix(map.root);
  map.floating = map.floating.filter((f) => f && typeof f === 'object');
  for (const f of map.floating) { fix(f); f.x = Number(f.x) || 0; f.y = Number(f.y) || 0; }
  map.rels = map.rels.filter((r) => r && seen.has(r.from) && seen.has(r.to) && r.from !== r.to);
  for (const r of map.rels) if (!r.id) r.id = uid();
  return map;
}

// ---- looking things up ----------------------------------------------------------

/** id -> { node, parent (node or null), top (the tree's top node), depth } for every topic. */
export function indexMap(map) {
  const idx = new Map();
  const walk = (nd, parent, top, depth) => {
    idx.set(nd.id, { node: nd, parent, top, depth });
    for (const c of nd.children) walk(c, nd, top, depth + 1);
  };
  walk(map.root, null, map.root, 0);
  for (const f of map.floating) walk(f, null, f, 0);
  return idx;
}

export const tops = (map) => [map.root, ...map.floating];
export const isTop = (map, id) => map.root.id === id || map.floating.some((f) => f.id === id);

export function* walkAll(map) {
  const stack = [...tops(map)].reverse();
  while (stack.length) { const nd = stack.pop(); yield nd; for (let i = nd.children.length - 1; i >= 0; i--) stack.push(nd.children[i]); }
}
export function count(nd) { let k = 1; for (const c of nd.children) k += count(c); return k; }
export function descendants(nd) { let k = 0; for (const c of nd.children) k += count(c); return k; }
export function isInside(idx, id, ancestorId) {
  for (let e = idx.get(id); e && e.parent; e = idx.get(e.parent.id)) if (e.parent.id === ancestorId) return true;
  return false;
}
export function ancestors(idx, id) {
  const out = [];
  for (let e = idx.get(id); e && e.parent; e = idx.get(e.parent.id)) out.push(e.parent);
  return out;
}
/** A topic's siblings array and its position in it (floating topics: map.floating). */
export function siblingsOf(map, idx, id) {
  const e = idx.get(id);
  if (!e) return null;
  const arr = e.parent ? e.parent.children : (map.root.id === id ? null : map.floating);
  return arr ? { arr, i: arr.findIndex((x) => x.id === id) } : null;
}

// ---- edits (all mutate the map; the editor snapshots it first for undo) -----------

export function addChild(map, idx, parentId, text = '', at = -1) {
  const p = idx.get(parentId)?.node;
  if (!p) return null;
  const c = node(text);
  if (at < 0 || at > p.children.length) p.children.push(c); else p.children.splice(at, 0, c);
  p.collapsed = false;
  return c;
}

/** A new sibling after (or before) the topic. The central topic and floating topics get a child instead. */
export function addSibling(map, idx, id, before = false) {
  const e = idx.get(id);
  if (!e) return null;
  if (!e.parent) return addChild(map, idx, id);
  const arr = e.parent.children;
  const i = arr.findIndex((x) => x.id === id);
  const c = node('');
  if (e.parent === map.root && e.node.side) c.side = e.node.side;
  arr.splice(before ? i : i + 1, 0, c);
  return c;
}

/** Put a new topic between this one and its parent. */
export function addParent(map, idx, id) {
  const e = idx.get(id);
  if (!e || !e.parent) return null;
  const arr = e.parent.children;
  const i = arr.findIndex((x) => x.id === id);
  const p = node('');
  if (e.node.side) { p.side = e.node.side; delete e.node.side; }
  arr.splice(i, 1, p);
  p.children.push(e.node);
  return p;
}

/** Delete topics (and their branches). The central topic stays. Returns what was removed. */
export function removeNodes(map, idx, ids) {
  const want = new Set(ids.filter((id) => id !== map.root.id));
  // a topic inside another removed topic goes with it
  for (const id of [...want]) if (ancestors(idx, id).some((a) => want.has(a.id))) want.delete(id);
  const gone = new Set();
  for (const id of want) {
    const e = idx.get(id);
    if (!e) continue;
    const arr = e.parent ? e.parent.children : map.floating;
    const i = arr.findIndex((x) => x.id === id);
    if (i >= 0) arr.splice(i, 1);
    const mark = (nd) => { gone.add(nd.id); nd.children.forEach(mark); };
    mark(e.node);
  }
  map.rels = map.rels.filter((r) => !gone.has(r.from) && !gone.has(r.to));
  return gone;
}

/** Can `id` move under `parentId`? (never into itself or its own branch) */
export function canMoveUnder(map, idx, id, parentId) {
  if (id === map.root.id || id === parentId) return false;
  if (!idx.get(parentId)) return false;
  return !isInside(idx, parentId, id);
}

/**
 * Move a topic (with its branch) under `parentId` at position `at` (-1 = last),
 * or to the canvas as a floating topic at { x, y } when parentId is null.
 */
export function moveNode(map, idx, id, parentId, at = -1, pos = null, side = null) {
  const e = idx.get(id);
  if (!e || id === map.root.id) return false;
  if (parentId && !canMoveUnder(map, idx, id, parentId)) return false;
  const from = e.parent ? e.parent.children : map.floating;
  const i = from.findIndex((x) => x.id === id);
  if (i < 0) return false;
  const nd = e.node;
  from.splice(i, 1);
  delete nd.x; delete nd.y;
  if (!parentId) {
    delete nd.side;
    nd.x = Math.round(pos?.x || 0); nd.y = Math.round(pos?.y || 0);
    map.floating.push(nd);
    return true;
  }
  const p = idx.get(parentId).node;
  let k = at;
  if (from === p.children && at > i) k -= 1; // the gap it left
  if (k < 0 || k > p.children.length) p.children.push(nd); else p.children.splice(k, 0, nd);
  if (p === map.root && side) nd.side = side;
  else if (p !== map.root) delete nd.side;
  p.collapsed = false;
  return true;
}

export function reorder(map, idx, id, delta) {
  const s = siblingsOf(map, idx, id);
  if (!s || s.arr === map.floating) return false;
  const j = s.i + delta;
  if (j < 0 || j >= s.arr.length) return false;
  const [nd] = s.arr.splice(s.i, 1);
  s.arr.splice(j, 0, nd);
  return true;
}

/** Shift+Tab: become the parent's next sibling. */
export function promote(map, idx, id) {
  const e = idx.get(id);
  if (!e || !e.parent) return false;
  const pe = idx.get(e.parent.id);
  if (!pe.parent) return false; // already a first-level topic
  const gp = pe.parent;
  return moveNode(map, idx, id, gp.id, gp.children.findIndex((x) => x.id === e.parent.id) + 1, null, e.parent.side || null);
}

/** Become the last child of the previous sibling. */
export function demote(map, idx, id) {
  const s = siblingsOf(map, idx, id);
  if (!s || s.arr === map.floating || s.i === 0) return false;
  return moveNode(map, idx, id, s.arr[s.i - 1].id, -1);
}

/** A deep copy of a branch with fresh ids (for copy, paste and duplicate). */
export function cloneBranch(nd, keepPos = false) {
  const c = { ...nd, id: uid(), children: nd.children.map((k) => cloneBranch(k)) };
  if (!keepPos) { delete c.x; delete c.y; }
  delete c.gx; delete c.gy; delete c.pin; // a copy finds its own place on a bubble map
  return c;
}

export function setCollapsedDeep(nd, collapsed, fromDepth = 0, depth = 0) {
  if (nd.children.length && depth >= fromDepth) nd.collapsed = collapsed;
  else if (!collapsed) nd.collapsed = false;
  for (const c of nd.children) setCollapsedDeep(c, collapsed, fromDepth, depth + 1);
}
/** Show exactly `level` levels under the central topic (and under each floating topic). */
export function expandToLevel(map, level) {
  const go = (nd, depth) => { if (nd.children.length) nd.collapsed = depth >= level; for (const c of nd.children) go(c, depth + 1); };
  for (const t of tops(map)) go(t, 0);
}

/** Done / total over a topic's task children (and deeper), or null when none are tasks. */
export function taskProgress(nd) {
  let done = 0; let total = 0;
  const walk = (x) => { for (const c of x.children) { if (c.task) { total++; if (c.task === 'done') done++; } walk(c); } };
  walk(nd);
  return total ? { done, total } : null;
}

export function findMatches(map, q) {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return [];
  const out = [];
  for (const nd of walkAll(map)) if (nd.text.toLowerCase().includes(s) || (nd.note || '').toLowerCase().includes(s)) out.push(nd.id);
  return out;
}
